import React, { useState, useEffect } from 'react';
import { DollarOutlined, DownOutlined, CalendarOutlined, SettingOutlined, PlusOutlined, DeleteOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { Select, InputNumber, DatePicker, Switch, Button, message } from 'antd';
import dayjs from 'dayjs';

const MetaAdSetBudgetSchedule = ({ campaignData, onUpdate, onNext }) => {
  const [budgetType, setBudgetType] = useState(campaignData.metaAds?.adSetBudgetType || 'DAILY');
  const [budgetAmount, setBudgetAmount] = useState(campaignData.metaAds?.adSetBudgetAmount || '');
  const [currency, setCurrency] = useState(campaignData.metaAds?.adSetCurrency || 'USD');
  const [bidStrategy, setBidStrategy] = useState(campaignData.metaAds?.adSetBidStrategy || 'HIGHEST_VOLUME');
  const [startDate, setStartDate] = useState(campaignData.metaAds?.adSetStartDate ? dayjs(campaignData.metaAds.adSetStartDate) : null);
  const [endDate, setEndDate] = useState(campaignData.metaAds?.adSetEndDate ? dayjs(campaignData.metaAds.adSetEndDate) : null);
  const [budgetSchedulingEnabled, setBudgetSchedulingEnabled] = useState(campaignData.metaAds?.adSetBudgetSchedulingEnabled || false);
  const [budgetTimeSlots, setBudgetTimeSlots] = useState(campaignData.metaAds?.adSetBudgetTimeSlots || []);

  // Days of the week for Meta scheduling
  const daysOfWeek = [
    { value: 'MON', label: 'Monday' },
    { value: 'TUE', label: 'Tuesday' },
    { value: 'WED', label: 'Wednesday' },
    { value: 'THU', label: 'Thursday' },
    { value: 'FRI', label: 'Friday' },
    { value: 'SAT', label: 'Saturday' },
    { value: 'SUN', label: 'Sunday' },
  ];

  // Time options (hourly slots)
  const timeSlots = Array.from({ length: 24 }, (_, i) => ({
    value: `${i.toString().padStart(2, '0')}:00`,
    label: `${i === 0 ? '12' : i > 12 ? i - 12 : i}:00 ${i < 12 || i === 24 ? 'AM' : 'PM'}`,
  }));

  // Budget increase percentage options
  const budgetIncreaseOptions = [
    { value: 25, label: '25% increase' },
    { value: 50, label: '50% increase' },
    { value: 75, label: '75% increase' },
    { value: 100, label: '100% increase (2x)' },
    { value: 150, label: '150% increase (2.5x)' },
    { value: 200, label: '200% increase (3x)' },
  ];

  const addTimeSlot = () => {
    const newSlot = {
      id: Date.now(),
      days: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
      startTime: '09:00',
      endTime: '17:00',
      budgetIncrease: 50,
    };
    setBudgetTimeSlots([...budgetTimeSlots, newSlot]);
  };

  const removeTimeSlot = (slotId) => {
    setBudgetTimeSlots(budgetTimeSlots.filter(slot => slot.id !== slotId));
  };

  const updateTimeSlot = (slotId, field, value) => {
    setBudgetTimeSlots(budgetTimeSlots.map(slot => 
      slot.id === slotId ? { ...slot, [field]: value } : slot
    ));
  };

  const updateSlotDays = (slotId, days) => {
    setBudgetTimeSlots(budgetTimeSlots.map(slot => 
      slot.id === slotId ? { ...slot, days } : slot
    ));
  };

  // Keep parent data in sync as fields change (single-page campaign view)
  useEffect(() => {
    onUpdate({
      metaAds: {
        adSetBudgetType: budgetType,
        adSetBudgetAmount: budgetAmount,
        adSetCurrency: currency,
        adSetBidStrategy: bidStrategy,
        adSetStartDate: startDate?.toISOString(),
        adSetEndDate: endDate?.toISOString(),
        adSetBudgetSchedulingEnabled: budgetSchedulingEnabled,
        adSetBudgetTimeSlots: budgetTimeSlots,
      }
    });
  }, [budgetType, budgetAmount, currency, bidStrategy, startDate, endDate, budgetSchedulingEnabled, budgetTimeSlots]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = () => {
    if (!budgetAmount || budgetAmount <= 0) {
      message.warning('Please enter a valid budget amount');
      return;
    }

    if (budgetType === 'LIFETIME' && !endDate) {
      message.warning('End date is required for lifetime budgets');
      return;
    }

    if (budgetSchedulingEnabled && budgetTimeSlots.length === 0) {
      message.warning('Please add at least one time slot or disable budget scheduling');
      return;
    }

    onUpdate({
      metaAds: {
        ...campaignData.metaAds,
        adSetBudgetType: budgetType,
        adSetBudgetAmount: budgetAmount,
        adSetCurrency: currency,
        adSetBidStrategy: bidStrategy,
        adSetStartDate: startDate?.toISOString(),
        adSetEndDate: endDate?.toISOString(),
        adSetBudgetSchedulingEnabled: budgetSchedulingEnabled,
        adSetBudgetTimeSlots: budgetTimeSlots,
      }
    });
    onNext();
  };

  return (
    <div className="step-container" style={{ maxWidth: '900px', margin: '0 auto', padding: '0 20px 100px' }}>
      {/* Header with Icon */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px',
        }}>
          <DollarOutlined style={{ fontSize: '32px', color: '#111827' }} />
        </div>

        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: '#000',
          marginBottom: '12px',
          textAlign: 'center'
        }}>
          Ad Set Budget & Schedule
        </h1>
        <p style={{
          fontSize: '16px',
          color: 'var(--ink-200)',
          marginBottom: 0,
          textAlign: 'center'
        }}>
          Configure budget and scheduling for your ad set
        </p>
      </div>

      {/* Budget + Schedule side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start', marginBottom: '24px' }}>

      {/* Budget Configuration */}
      <div style={{
        background: '#fff',
        border: '1px solid var(--paper-300)',
        borderRadius: '12px',
        padding: '24px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '16px',
          }}>
            <SettingOutlined style={{ fontSize: '24px', color: 'var(--ink-200)' }} />
          </div>
          <div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '4px'
            }}>
              Budget Configuration
            </h3>
            <p style={{
              fontSize: '14px',
              color: 'var(--ink-200)',
              margin: 0
            }}>
              Set your ad set budget and bidding strategy
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Budget Type */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '8px'
            }}>
              Budget Type
            </label>
            <Select
              value={budgetType}
              onChange={setBudgetType}
              style={{ width: '100%' }}
              size="large"
              options={[
                { value: 'DAILY', label: 'Daily Budget' },
                { value: 'LIFETIME', label: 'Lifetime Budget' },
              ]}
            />
          </div>

          {/* Budget Amount & Currency */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '8px'
            }}>
              Budget Amount
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <InputNumber
                value={budgetAmount}
                onChange={setBudgetAmount}
                placeholder="0.00"
                min={0}
                precision={2}
                style={{ flex: 1 }}
                size="large"
                keyboard
                addonBefore="$"
                parser={(val) => val?.replace(/[^0-9.]/g, '')}
                formatter={(val) => val ? `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
              />
              <Select
                value={currency}
                onChange={setCurrency}
                style={{ width: '80px' }}
                size="large"
                options={[
                  { value: 'USD', label: 'USD' },
                  { value: 'EUR', label: 'EUR' },
                  { value: 'GBP', label: 'GBP' },
                  { value: 'CAD', label: 'CAD' },
                ]}
              />
            </div>
          </div>

          {/* Bid Strategy */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '8px'
            }}>
              Bid Strategy
            </label>
            <Select
              value={bidStrategy}
              onChange={setBidStrategy}
              style={{ width: '100%' }}
              size="large"
              options={[
                { value: 'HIGHEST_VOLUME', label: 'Highest Volume' },
                { value: 'COST_PER_RESULT_GOAL', label: 'Cost Per Result Goal' },
                { value: 'BID_CAP', label: 'Bid Cap' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Schedule Configuration */}
      <div style={{
        background: '#fff',
        border: '1px solid var(--paper-300)',
        borderRadius: '12px',
        padding: '24px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '16px',
          }}>
            <CalendarOutlined style={{ fontSize: '24px', color: 'var(--ink-200)' }} />
          </div>
          <div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '4px'
            }}>
              Schedule Configuration
            </h3>
            <p style={{
              fontSize: '14px',
              color: 'var(--ink-200)',
              margin: 0
            }}>
              Set start and end dates for your ad set
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          {/* Start Date */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '8px'
            }}>
              Start Date & Time
            </label>
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              showTime
              format="YYYY-MM-DD HH:mm"
              placeholder="Select start date & time"
              style={{ width: '100%' }}
              size="large"
            />
          </div>

          {/* End Date */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '8px'
            }}>
              End Date & Time
              {budgetType === 'DAILY' && <span style={{ fontSize: '12px', color: 'var(--ink-200)', fontWeight: 'normal' }}> (Optional)</span>}
              {budgetType === 'LIFETIME' && <span style={{ fontSize: '12px', color: '#ef4444', fontWeight: 'normal' }}> (Required)</span>}
            </label>
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              showTime
              format="YYYY-MM-DD HH:mm"
              placeholder={budgetType === 'LIFETIME' ? "Select end date & time" : "Optional for daily budget"}
              style={{ width: '100%' }}
              size="large"
            />
            {budgetType === 'LIFETIME' && !endDate && (
              <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                End date is required for lifetime budgets
              </div>
            )}
          </div>
        </div>

        {/* Budget Scheduling Toggle */}
        <div style={{
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid var(--paper-300)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CalendarOutlined style={{ fontSize: '20px', color: 'var(--ink-200)' }} />
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                  Budget Scheduling
                </div>
                <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
                  Add time slots with increased budget for better performance
                </div>
              </div>
            </div>
            <Switch
              checked={budgetSchedulingEnabled}
              onChange={setBudgetSchedulingEnabled}
              style={{
                background: budgetSchedulingEnabled ? 'var(--ink)' : 'var(--paper-300)',
              }}
            />
          </div>

          {/* Time Slots Section - Shows when toggle is ON */}
          {budgetSchedulingEnabled && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>Time Slots</div>
                <Button type="dashed" icon={<PlusOutlined />} onClick={addTimeSlot} size="small">Add Time Slot</Button>
              </div>

              {budgetTimeSlots.length === 0 ? (
                <div style={{ padding: '24px', background: '#f9fafb', borderRadius: '8px', textAlign: 'center', border: '1px dashed var(--paper-300)' }}>
                  <ClockCircleOutlined style={{ fontSize: '24px', color: '#9ca3af', marginBottom: '8px' }} />
                  <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>No time slots added yet</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Click "Add Time Slot" to schedule budget increases</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {budgetTimeSlots.map((slot, index) => (
                    <div key={slot.id} style={{ padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#0369a1' }}>Time Slot {index + 1}</span>
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeTimeSlot(slot.id)} size="small" />
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: 'var(--ink-200)', marginBottom: '6px' }}>Days</label>
                        <Select mode="multiple" value={slot.days} onChange={(days) => updateSlotDays(slot.id, days)} style={{ width: '100%' }} size="small">
                          {daysOfWeek.map(day => <Select.Option key={day.value} value={day.value}>{day.label}</Select.Option>)}
                        </Select>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', color: 'var(--ink-200)', marginBottom: '6px' }}>Start Time</label>
                          <Select value={slot.startTime} onChange={(time) => updateTimeSlot(slot.id, 'startTime', time)} style={{ width: '100%' }} size="small">
                            {timeSlots.map(time => <Select.Option key={time.value} value={time.value}>{time.label}</Select.Option>)}
                          </Select>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', color: 'var(--ink-200)', marginBottom: '6px' }}>End Time</label>
                          <Select value={slot.endTime} onChange={(time) => updateTimeSlot(slot.id, 'endTime', time)} style={{ width: '100%' }} size="small">
                            {timeSlots.map(time => <Select.Option key={time.value} value={time.value}>{time.label}</Select.Option>)}
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: 'var(--ink-200)', marginBottom: '6px' }}>Budget Increase</label>
                        <Select value={slot.budgetIncrease} onChange={(increase) => updateTimeSlot(slot.id, 'budgetIncrease', increase)} style={{ width: '100%' }} size="small">
                          {budgetIncreaseOptions.map(option => <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>)}
                        </Select>
                      </div>

                      <div style={{ marginTop: '12px', padding: '8px 12px', background: '#dbeafe', borderRadius: '6px', fontSize: '12px', color: '#1e40af' }}>
                        💰 Budget during this time: <strong>${(budgetAmount * (1 + slot.budgetIncrease / 100)).toFixed(2)}</strong> (${budgetAmount} + {slot.budgetIncrease}%)
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      </div>{/* end grid */}

      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid var(--paper-300)',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10
      }}>
        <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
          Building: Ad Set Budget & Schedule
        </div>
        <button
          onClick={handleContinue}
          style={{
            padding: '12px 32px',
            background: (budgetAmount && budgetAmount > 0) ? 'var(--ink-200)' : 'var(--paper-300)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: (budgetAmount && budgetAmount > 0) ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (budgetAmount && budgetAmount > 0) {
              e.currentTarget.style.background = '#4b5563';
            }
          }}
          onMouseLeave={(e) => {
            if (budgetAmount && budgetAmount > 0) {
              e.currentTarget.style.background = 'var(--ink-200)';
            }
          }}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default MetaAdSetBudgetSchedule;
